import { Methods } from '../../api/methods.js';
import { ZaparooClient } from '../../client/client.js';
import { resolvePaths } from '../../client/config.js';
import { RpcError } from '../../client/errors.js';
import { resolveDevice } from '../../client/resolver.js';
import { performPairing } from '../../crypto/pairing.js';
import { CredentialStore } from '../../crypto/storage.js';
import type { ParsedArgs } from '../args.js';
import { flag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { readSecret } from '../secret.js';
import { pickDefined, validatePairRole, withClient } from './common.js';

export async function pairCommand(args: ParsedArgs): Promise<CommandResult> {
  const requestedAction = args.positionals[1] ?? 'status';
  const action = requestedAction === 'start' ? 'complete' : requestedAction;
  const paths = resolvePaths(args.options.configPath, args.options.credentialsPath);
  const store = new CredentialStore(paths.credentialsPath);

  if (action === 'list') {
    const credentials = store.listCredentials();
    const data = Object.entries(credentials).map(([device, entry]) => ({
      device,
      paired: true,
      clientId: entry.clientId,
      clientName: entry.clientName,
      aliases: entry.aliases ?? [],
      createdAt: entry.createdAt,
    }));
    return { data, human: data.map((entry) => entry.device).join('\n') };
  }

  const device = await resolveDevice(args.options);
  switch (action) {
    case 'status':
      return pairStatus(args, store, device);
    case 'begin': {
      const role = validatePairRole(flag(args.flags, 'role'));
      const data = await withClient(args.options, (client) =>
        client.request(Methods.ClientsPairStart, pickDefined({ role })),
      );
      return {
        data,
        human: `Pairing started on Core. Complete with: zaparoo-cli pair complete --pin <pin> --device ${device.id}`,
      };
    }
    case 'cancel': {
      const data = await withClient(args.options, (client) =>
        client.request(Methods.ClientsPairCancel),
      );
      return { data, human: 'Pairing cancelled on Core' };
    }
    case 'complete': {
      const pin = flag(args.flags, 'pin') ?? (await readSecret('Pairing PIN: '));
      const clientName = flag(args.flags, 'name') ?? 'zaparoo-cli';
      if (!pin) {
        throw new CliError(
          `pair ${requestedAction} requires --pin <123456> or stdin`,
          ExitCode.Usage,
        );
      }
      try {
        const result = await performPairing(
          device.host,
          device.port,
          pin,
          clientName,
          args.options.timeoutSeconds * 1000,
          device.scheme === 'wss' ? 'https' : 'http',
        );
        store.saveCredentials(device.id, result.authToken, result.pairingKey, {
          clientId: result.clientId,
          clientName,
          aliases: device.aliases,
        });
        const credentials = store.getCredentials(device.id, device.aliases);
        const verification = credentials
          ? await verifyCredentials(args, device, credentials).catch((error) => ({
              verified: false,
              error: error instanceof Error ? error.message : String(error),
            }))
          : { verified: false, error: 'credentials were not saved' };
        return {
          data: {
            device: device.id,
            paired: true,
            clientId: result.clientId,
            verification,
          },
          human: `Paired ${device.id}`,
        };
      } catch (error) {
        throw new CliError(
          error instanceof Error ? error.message : String(error),
          ExitCode.Pairing,
        );
      }
    }
    case 'forget': {
      let deleted = false;
      for (const candidate of new Set([device.id, ...(device.aliases ?? [])])) {
        deleted = store.deleteCredentials(candidate) || deleted;
      }
      return {
        data: { device: device.id, deleted },
        human: deleted ? `Forgot ${device.id}` : `No credentials for ${device.id}`,
      };
    }
    default:
      throw new CliError(`Unknown pair action "${requestedAction}"`, ExitCode.Usage);
  }
}

async function pairStatus(
  args: ParsedArgs,
  store: CredentialStore,
  device: Awaited<ReturnType<typeof resolveDevice>>,
): Promise<CommandResult> {
  const credentials = store.getCredentials(device.id, device.aliases);
  let connection: Record<string, unknown>;
  if (credentials) {
    try {
      connection = await verifyCredentials(args, device, credentials);
    } catch (error) {
      connection = {
        verified: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  } else {
    const client = new ZaparooClient(device, {
      connectTimeoutMs: args.options.timeoutSeconds * 1000,
      requestTimeoutMs: args.options.timeoutSeconds * 1000,
    });
    try {
      const version = await client.connect();
      connection = { verified: true, encrypted: false, version };
    } catch (error) {
      connection = {
        verified: false,
        error: error instanceof Error ? error.message : String(error),
        kind:
          error && typeof error === 'object' && 'kind' in error
            ? (error as { kind: unknown }).kind
            : undefined,
      };
    } finally {
      await client.close();
    }
  }
  const data = {
    device: device.id,
    storedCredentials: credentials !== undefined,
    clientId: credentials?.clientId,
    clientName: credentials?.clientName,
    connection,
  };
  return {
    data,
    human: `${device.id}: ${credentials ? 'credentials stored' : 'not paired'}`,
  };
}

async function verifyCredentials(
  args: ParsedArgs,
  device: Awaited<ReturnType<typeof resolveDevice>>,
  credentials: NonNullable<ReturnType<CredentialStore['getCredentials']>>,
): Promise<Record<string, unknown>> {
  const client = new ZaparooClient(device, {
    credentials,
    connectTimeoutMs: args.options.timeoutSeconds * 1000,
    requestTimeoutMs: args.options.timeoutSeconds * 1000,
  });
  try {
    const version = await client.connect();
    try {
      const current = await client.request(Methods.ClientsCurrent);
      return { verified: true, encrypted: true, version, currentSupported: true, current };
    } catch (error) {
      if (error instanceof RpcError && error.rpc.code === -32601) {
        return { verified: true, encrypted: true, version, currentSupported: false };
      }
      throw error;
    }
  } finally {
    await client.close();
  }
}
